import React, { useEffect, useState } from 'react';
import API from '../../api';
import Table from '../smallComponent/Table';
import CustomerFilterBar from '../smallComponent/CustomerFilterBar';
import * as XLSX from 'xlsx';
import MessageModal from '../smallComponent/MessageModal';
import { useCustomers } from '../context/CustomerContext';

function FollowupList() {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showAll, setShowAll] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalType, setModalType] = useState('info');
  const [modalTitle, setModalTitle] = useState('Message');

  const { allCustomers, fetchCustomers } = useCustomers();

  const pageSize = 10;

  // ✅ Initial fetch
  useEffect(() => {
    fetchCustomers();
  }, []);

  // ✅ Jab bhi allCustomers update hoga
  useEffect(() => {
    if (allCustomers && allCustomers.length > 0) {
      const filtered = allCustomers.filter((c) => c.status === 0);
      setCustomers(filtered);
      setFilteredCustomers(filtered);
      setCurrentPage(1);
    }
    setLoading(false);
  }, [allCustomers]);

  // ✅ Search / Date filter apply karna
  useEffect(() => {
    handleFilter();
  }, [search, startDate, endDate, customers]);

  const handleFilter = () => {
    const filtered = customers.filter((c) => {
      const s = search.toLowerCase();

      const nameMatch =
        !s ||
        c.customer_name?.toLowerCase().includes(s) ||
        c.contact_number?.toString().includes(s) ||
        c.business_name?.toLowerCase().includes(s) ||
        c.salesman_name?.toLowerCase().includes(s);

      const date = new Date(c.created_at);
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : null;

      if (start) start.setHours(0, 0, 0, 0);
      if (end) end.setHours(23, 59, 59, 999);

      const dateMatch = (!start || date >= start) && (!end || date <= end);

      return nameMatch && dateMatch;
    });

    setFilteredCustomers(filtered);
    setCurrentPage(1);
  };

  const onReset = () => {
    setSearch('');
    setStartDate('');
    setEndDate('');
  };

  const onExport = () => {
    const exportData = filteredCustomers.map((c, i) => ({
      SN: i + 1,
      Name: c.customer_name,
      Phone: c.contact_number,
      Business: c.business_name,
      Source: c.source,
      Salesman: c.salesman_name,
      'Created At': c.created_at,
      'Future Call': c.future_call_date,
      'Lead %': c.lead_parsent,
      Status: c.status,
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Customers');
    XLSX.writeFile(workbook, 'customers_export.xlsx');
  };

  const updateFutureCall = async (id, date, customersList, setCustomersFn, setFilteredFn) => {
    try {
      await API.post(`/customers/${id}/futurecall`, { future_call_date: date });
      const updated = customersList.map((c) =>
        c.id === id ? { ...c, future_call_date: date, new_future_call_date: '' } : c
      );
      setCustomersFn(updated);
      setFilteredFn(updated);

      setModalTitle('Success');
      setModalMessage('Future call date updated successfully!');
      setModalType('success');
      setShowModal(true);
    } catch {
      setModalTitle('Error');
      setModalMessage('Failed to update future call date');
      setModalType('error');
      setShowModal(true);
    }
  };

  const updateLeadParsent = async (id, percent) => {
    try {
      await API.put(`/customers/${id}/lead-parsent`, { lead_parsent: percent });
    } catch {
      alert('Failed to update lead %');
    }
  };

  const markNI = async (id) => {
    if (!window.confirm('Mark as Not Interested?')) return;
    try {
      await API.put(`/customers/${id}/mark-ni`);
      fetchCustomers(); // refresh list after marking NI
    } catch {
      alert('Failed to mark NI');
    }
  };

  const renderProgressBar = (percent) => {
    let color = 'bg-green-500';
    if (percent <= 30) color = 'bg-red-600';
    else if (percent <= 70) color = 'bg-yellow-400';
    return (
      <div className="mb-2 h-5 w-full border-2 border-black rounded overflow-hidden">
        <div
          className={`${color} h-full text-center text-white text-xs`}
          style={{ width: `${percent}%` }}
        >
          {percent}%
        </div>
      </div>
    );
  };

  const indexOfLast = currentPage * pageSize;
  const indexOfFirst = indexOfLast - pageSize;
  const currentCustomers = showAll
    ? filteredCustomers
    : filteredCustomers.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredCustomers.length / pageSize);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <div className="container mx-auto mt-4 px-4">
      <h4 className="mb-3 text-xl font-semibold">Customer Follow-Up & Status</h4>

      <CustomerFilterBar
        search={search}
        setSearch={setSearch}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        onReset={onReset}
        onExport={onExport}
      />

      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <Table
            customers={currentCustomers}
            updateFutureCall={(id, date) =>
              updateFutureCall(id, date, customers, setCustomers, setFilteredCustomers)
            }
            updateLeadParsent={updateLeadParsent}
            renderProgressBar={renderProgressBar}
            markNI={markNI}
            setCustomers={setCustomers}
          />

          <div className="flex justify-between items-center mt-3">
            <span className="text-gray-600">
              {showAll
                ? `Showing All (${filteredCustomers.length})`
                : `Page ${currentPage} of ${totalPages}`}
            </span>

            <div className="flex space-x-2">
              {!showAll && (
                <>
                  <button
                    className="px-3 py-1 border rounded text-sm text-blue-600 hover:bg-blue-100 disabled:opacity-50"
                    disabled={currentPage === 1}
                    onClick={() => goToPage(currentPage - 1)}
                  >
                    ◀ Prev
                  </button>
                  <button
                    className="px-3 py-1 border rounded text-sm text-blue-600 hover:bg-blue-100 disabled:opacity-50"
                    disabled={currentPage === totalPages}
                    onClick={() => goToPage(currentPage + 1)}
                  >
                    Next ▶
                  </button>
                </>
              )}
              <button
                className={`px-3 py-1 rounded text-sm ${
                  showAll
                    ? 'bg-gray-400 text-white'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
                onClick={() => setShowAll(!showAll)}
              >
                {showAll ? 'Show Paginated' : 'Show All'}
              </button>
            </div>
          </div>
        </>
      )}

      <MessageModal
        show={showModal}
        onClose={() => setShowModal(false)}
        title={modalTitle}
        message={modalMessage}
        type={modalType}
      />
    </div>
  );
}

export default FollowupList;
